// AddCruxHereButton — sichtbar NUR wenn der User im Profile "on-device admin
// mode" aktiviert + Token gesetzt hat. Nimmt die aktuelle alongTrackMeters-
// Position (von nearestPointOnTrack im MapPage berechnet) und PATCHt sie als
// neuen Crux-Point ans Backend. Vor dem Senden fragen wir optional nach einer
// kurzen Note ("Root drop", "Switchback blind" …) — via window.prompt, damit
// wir keinen zusätzlichen Modal-Stack brauchen; das ist UX-schlicht aber im
// Feld (draußen, mit Handschuhen) wollten wir's nicht komplizierter machen.
//
// User-Wunsch Batch 7 (on-device admin crux):
//   "wenn ich mit dem handy unterwegs bin, mit der app, sollte ich auch als
//   admin im trail anhalten können und die aktuelle location als crux zum
//   trail hinzufügen können. das wäre sehr praktikabel. entfernung soll
//   dennoch im admin gehen, wenn man das möchte (gut am komputer), aber vor
//   ort viel besser zu pflegen, ich halte an und gebe es ein, DA wo es
//   gefährlich ist."
//
// Edge-cases:
// - ohne GPS-Fix (progressMeters == null): Button zeigt "Need GPS" disabled.
// - off-track weit (progress.offTrackMeters > 50m): Button warnt vor dem Senden,
//   der User kann abbrechen (vermeidet versehentliches Setzen aus dem Auto
//   auf Heimweg).
// - während pending: spinner + disabled.
// - nach Erfolg: Kurze grüne Erfolgs-Pill (~1.5s), dann zurück auf default.

import { useState } from 'react';
import type { CruxPoint } from '@adtu/shared';
import { appendCruxPoint } from '../lib/admin';
import { useAdminMode, useAdminToken } from '../lib/useUiSettings';

export interface AddCruxHereButtonProps {
  trailId: string;
  existingCruxes: CruxPoint[];
  /** Aktuelle GPS-Distanz entlang des Tracks in Metern, oder null wenn kein Fix. */
  alongTrackMeters: number | null;
  /** Off-Track-Distanz der aktuellen GPS-Position in Metern, oder null. */
  offTrackMeters: number | null;
  /**
   * Auflösende Höhe (ü.M.) an dieser Stelle. MapPage baut den Wert mit
   * Priorität (1) GPS-Altitude, (2) SRTM-Sample am Distanz-Punkt, (3) null.
   * Wird mit dem CruxPoint gespeichert — User sieht im Popup Distanz UND
   * Höhe (Bike-Computer-Parität: Sportler haben Uhr/Bike-Computer, wissen
   * wie weit oben sie sind → können die Crux antizipieren).
   */
  elevationMeters?: number | null;
  /** Callback — der Server-Stand der cruxPoints nach erfolgreichem Update. */
  onUpdated: (cruxes: CruxPoint[]) => void;
  /** 'compact' sitzt in der Button-Row neben »Ride«; 'floating' im Ride-Strip. */
  variant?: 'compact' | 'floating';
}

export function AddCruxHereButton(props: AddCruxHereButtonProps) {
  const { trailId, existingCruxes, alongTrackMeters, offTrackMeters, elevationMeters, onUpdated, variant = 'compact' } = props;
  const [adminMode] = useAdminMode();
  const [adminToken] = useAdminToken();
  const [pending, setPending] = useState(false);
  const [flash, setFlash] = useState<'success' | 'error' | 'pending' | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Rendern nur im Admin-Mode + mit Token. Sonst NULL — kein Platz-Fresser.
  if (!adminMode || !adminToken) return null;

  const hasFix = alongTrackMeters != null;
  const farOff = (offTrackMeters ?? 0) > 50;

  const onClick = async () => {
    if (pending || !hasFix) return;
    if (farOff) {
      const ok = window.confirm(
        `You are ~${Math.round(offTrackMeters ?? 0)} m off the trail. Add a crux at the nearest trail point anyway?`,
      );
      if (!ok) return;
    }
    // Optional Note — window.prompt, weil wir keinen Modal-Stack aufbauen wollen.
    // Leer bzw. abgebrochen → kein Note-Text (empty → null serverseitig).
    const note = window.prompt('Optional note (e.g. "Root drop", leave empty to skip):', '');
    // Nur bei *echtem* Abbruch (null), nicht bei leerem String: User kann
    // legitim "keine Note" wollen, aber wir sehen "" als "Skip"-Fall.
    if (note === null) return;

    setPending(true);
    setErrorMsg(null);
    try {
      const result = await appendCruxPoint(trailId, adminToken, existingCruxes, {
        distanceMeters: alongTrackMeters!,
        note: note.trim() || null,
        elevationMeters:
          elevationMeters != null && Number.isFinite(elevationMeters)
            ? elevationMeters
            : null,
      });
      // Auch bei synced=false hat der User den Crux lokal drin (optimistisch
      // persistiert) — deshalb rufen wir onUpdated IMMER, damit die Markers
      // sofort auf der Karte erscheinen.
      onUpdated(result.cruxPoints);
      if (result.synced) {
        setFlash('success');
        setTimeout(() => setFlash(null), 1500);
      } else {
        // Offline / Token kaputt: Crux ist lokal gespeichert und wird beim
        // nächsten Online-Event automatisch gepusht. UX zeigt "Pending" als
        // gelben Erfolgs-Zustand, damit der User den Unterschied sieht.
        setFlash('pending');
        setErrorMsg(
          result.error?.includes('admin_token_invalid')
            ? 'Saved offline — token rejected, will retry.'
            : result.error?.includes('admin_token_missing')
            ? 'Saved offline — no token set.'
            : 'Saved offline — will sync when online.',
        );
        setTimeout(() => setFlash(null), 3000);
      }
    } catch (err) {
      // appendCruxPoint wirft nur bei IDB-Fehlern (Incognito / Storage blocked).
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[admin] add-crux failed', msg);
      setErrorMsg('Storage blocked — crux not saved.');
      setFlash('error');
      setTimeout(() => setFlash(null), 3000);
    } finally {
      setPending(false);
    }
  };

  const label = pending
    ? 'Saving…'
    : flash === 'success'
    ? 'Saved ✓'
    : flash === 'pending'
    ? 'Saved (offline)'
    : !hasFix
    ? 'Need GPS'
    : 'Mark crux here';

  const title = pending
    ? 'Saving…'
    : flash === 'success'
    ? 'Key section saved'
    : !hasFix
    ? 'Waiting for GPS fix — tap Locate to start tracking'
    : `Tag current GPS position as a key section on this trail (${Math.round(alongTrackMeters ?? 0)} m from start)`;

  // Floating-Variante (Ride-Mini-Strip): runder Pin, 36px, rotgefärbt damit
  // er zwischen den Crux-Marker-Dreiecken visuell klar als "Aktion" lesbar ist.
  if (variant === 'floating') {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label={title}
        disabled={pending || !hasFix}
        className={`px-2.5 h-9 flex items-center justify-center gap-1.5 rounded-full text-[11px] font-semibold transition-colors pointer-events-auto ${
          flash === 'success'
            ? 'bg-emerald-500/90 text-white'
            : flash === 'pending'
            ? 'bg-amber-300/90 text-black'
            : flash === 'error'
            ? 'bg-red-600/90 text-white'
            : !hasFix
            ? 'bg-turbo-900/50 text-white/50 cursor-not-allowed'
            : 'bg-amber-500/90 hover:bg-amber-400 text-black shadow-md'
        }`}
      >
        <PlusTriangleIcon />
        <span className="leading-tight">{label}</span>
      </button>
    );
  }

  return (
    <div className="flex flex-col flex-shrink-0">
      <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label={title}
        disabled={pending || !hasFix}
        className={`flex-shrink-0 transition-colors px-2.5 py-1.5 rounded-lg flex items-center justify-center gap-1.5 border font-medium text-[12px] leading-tight ${
          flash === 'success'
            ? 'bg-emerald-500/90 text-white border-emerald-400'
            : flash === 'pending'
            ? 'bg-amber-300/90 text-black border-amber-400'
            : flash === 'error'
            ? 'bg-red-600/90 text-white border-red-400'
            : !hasFix
            ? 'bg-turbo-700/20 text-white/40 border-white/10 cursor-not-allowed'
            : 'bg-amber-500/90 hover:bg-amber-400 active:bg-amber-600 text-black border-amber-400 shadow-sm'
        }`}
      >
        <PlusTriangleIcon />
        <span className="text-center whitespace-nowrap">{label}</span>
      </button>
      {errorMsg && (flash === 'error' || flash === 'pending') && (
        <span
          className={`text-[10px] mt-0.5 max-w-[160px] leading-tight ${
            flash === 'error' ? 'text-red-300' : 'text-amber-200'
          }`}
        >
          {errorMsg}
        </span>
      )}
    </div>
  );
}

/** Kleines Plus-in-Dreieck Icon — hazard triangle mit + statt ! als "hinzufügen". */
function PlusTriangleIcon() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" style={{ display: 'block' }}>
      <path
        d="M10 1.8 L18.6 17 L1.4 17 Z"
        fill="#facc15"
        stroke="#111827"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      {/* Plus-Zeichen in Schwarz */}
      <rect x="9" y="8" width="2" height="7" rx="0.4" fill="#111827" />
      <rect x="6.5" y="10.5" width="7" height="2" rx="0.4" fill="#111827" />
    </svg>
  );
}
